import { useMemo, useState } from 'react'
import {
  format,
  addDays,
  addWeeks,
  startOfISOWeek,
  getISOWeek,
  getISOWeekYear,
} from 'date-fns'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useIsMobile } from '@/hooks/useIsMobile'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  buildWeekOptions,
  dowIndex,
  getSameISOWeekPreviousYear,
  pctChange,
  type WeekOption,
} from '@/lib/isoWeek'

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

interface WeeklyComparisonChartProps {
  leadsByDate: { date: string; count: number }[]
}

type ChartRow = {
  day: string
  selectedWeek?: number
  previousWeek?: number
  yearAgo?: number
}

function weekLabel(weekNum: number, year: number): string {
  return `Week ${weekNum} (${year})`
}

export function WeeklyComparisonChart({ leadsByDate }: WeeklyComparisonChartProps) {
  const isMobile = useIsMobile()

  const today = new Date()
  const [selectedYear, setSelectedYear] = useState(() => getISOWeekYear(new Date()))
  const [selectedWeek, setSelectedWeek] = useState(() => getISOWeek(new Date()))

  const { years, weeksByYear, spanStart, spanEnd } = useMemo(
    () => buildWeekOptions(leadsByDate),
    [leadsByDate]
  )

  // Fall back to the most recent available week if the stored selection is not in the data span
  const weeksForYear: WeekOption[] =
    weeksByYear.get(selectedYear) ?? weeksByYear.get(years[0]) ?? []
  const selected: WeekOption | undefined =
    weeksForYear.find((w) => w.weekNum === selectedWeek) ?? weeksForYear[0]

  function handleYearChange(value: string) {
    const year = Number(value)
    const weeks = weeksByYear.get(year) ?? []
    setSelectedYear(year)
    // Keep the same week number where the year has one, otherwise its most recent week
    if (!weeks.some((w) => w.weekNum === selectedWeek)) {
      setSelectedWeek(weeks[0]?.weekNum ?? selectedWeek)
    }
  }

  const selectedMonday = selected?.monday ?? startOfISOWeek(today)

  const { chartData, totals, meta, hasPreviousWeek, hasYearAgo } = useMemo(() => {
    const dateMap = new Map(leadsByDate.map((d) => [d.date, d.count]))

    const previousMonday = addWeeks(selectedMonday, -1)
    const yearAgoMonday = getSameISOWeekPreviousYear(selectedMonday)

    // A comparison line is drawn whenever its week falls inside the known data span -
    // an in-span week with no leads is a real zero, not missing data
    const inSpan = (monday: Date) => monday >= spanStart && monday <= spanEnd
    const hasPreviousWeek = inSpan(previousMonday)
    const hasYearAgo = yearAgoMonday !== null && inSpan(yearAgoMonday)

    // Only the current week is partial - a week in the past is drawn in full
    const isCurrentWeek = selectedMonday.getTime() === startOfISOWeek(today).getTime()
    const lastIdx = isCurrentWeek ? Math.min(dowIndex(today), 5) : 5

    const chartData: ChartRow[] = DAY_LABELS.map((label, i) => {
      const row: ChartRow = { day: label }
      if (i <= lastIdx) {
        row.selectedWeek = dateMap.get(format(addDays(selectedMonday, i), 'yyyy-MM-dd')) ?? 0
      }
      if (hasPreviousWeek) {
        row.previousWeek = dateMap.get(format(addDays(previousMonday, i), 'yyyy-MM-dd')) ?? 0
      }
      if (hasYearAgo && yearAgoMonday) {
        row.yearAgo = dateMap.get(format(addDays(yearAgoMonday, i), 'yyyy-MM-dd')) ?? 0
      }
      return row
    })

    const sum = (monday: Date, upTo: number) => {
      let total = 0
      for (let i = 0; i <= upTo; i++) {
        total += dateMap.get(format(addDays(monday, i), 'yyyy-MM-dd')) ?? 0
      }
      return total
    }

    // Comparisons run against the same span of days, so a partial current week is
    // never measured against a full reference week
    const totalSelected     = sum(selectedMonday, lastIdx)
    const totalPrevious     = sum(previousMonday, 5)
    const partialPrevious   = sum(previousMonday, lastIdx)
    const totalYearAgo      = yearAgoMonday ? sum(yearAgoMonday, 5) : 0
    const partialYearAgo    = yearAgoMonday ? sum(yearAgoMonday, lastIdx) : 0

    return {
      chartData,
      totals: { totalSelected, totalPrevious, partialPrevious, totalYearAgo, partialYearAgo },
      meta: {
        selectedWeekNum:  getISOWeek(selectedMonday),
        selectedYearNum:  getISOWeekYear(selectedMonday),
        previousWeekNum:  getISOWeek(previousMonday),
        previousYearNum:  getISOWeekYear(previousMonday),
        yearAgoYear:      yearAgoMonday ? getISOWeekYear(yearAgoMonday) : getISOWeekYear(selectedMonday) - 1,
      },
      hasPreviousWeek,
      hasYearAgo,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadsByDate, selectedMonday.getTime(), spanStart, spanEnd])

  const selectedLabel = weekLabel(meta.selectedWeekNum, meta.selectedYearNum)
  const previousLabel = weekLabel(meta.previousWeekNum, meta.previousYearNum)
  const yearAgoLabel = weekLabel(meta.selectedWeekNum, meta.yearAgoYear)

  const description = [
    selectedLabel,
    hasPreviousWeek ? `vs week ${meta.previousWeekNum}` : null,
    hasYearAgo ? `and the same week in ${meta.yearAgoYear}` : null,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Card>
      <CardHeader>
        <div className={`flex gap-3 ${isMobile ? 'flex-col' : 'flex-row items-start justify-between'}`}>
          <div>
            <CardTitle>Weekly Lead Comparison</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>

          {/* Week selector */}
          <div className="flex gap-2">
            <Select value={String(selectedYear)} onValueChange={handleYearChange}>
              <SelectTrigger className="h-auto w-auto whitespace-nowrap px-2 py-1 [&>span]:line-clamp-none" aria-label="Year" hideIcon>
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={String(selected?.weekNum ?? '')} onValueChange={(v: string) => setSelectedWeek(Number(v))}>
              <SelectTrigger className="h-auto w-auto whitespace-nowrap px-2 py-1 [&>span]:line-clamp-none" aria-label="Week" hideIcon>
                <SelectValue placeholder="Week" />
              </SelectTrigger>
              <SelectContent>
                {weeksForYear.map((week) => (
                  <SelectItem key={week.weekNum} value={String(week.weekNum)}>
                    {week.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className={`flex gap-6 ${isMobile ? 'flex-col' : 'flex-row items-center'}`}>

          {/* Chart */}
          <div className="h-[260px] min-w-0 flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" padding={{ left: 24, right: 24 }} />
                <YAxis allowDecimals={false} width={30} />
                <Tooltip />
                <Legend />
                <Line
                  type="linear" dataKey="selectedWeek" name={selectedLabel}
                  stroke="#10B981" strokeWidth={2} dot={{ fill: '#10B981' }} connectNulls={false}
                />
                {hasPreviousWeek && (
                  <Line
                    type="linear" dataKey="previousWeek" name={previousLabel}
                    stroke="#3B82F6" strokeWidth={2} dot={{ fill: '#3B82F6' }}
                  />
                )}
                {hasYearAgo && (
                  <Line
                    type="linear" dataKey="yearAgo" name={yearAgoLabel}
                    stroke="#F59E0B" strokeWidth={2} dot={{ fill: '#F59E0B' }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Stats panel */}
          <div className={`flex flex-col gap-2 ${isMobile ? 'w-full' : 'w-65 shrink-0'}`}>

            {/* ── Selected week ── */}
            <div data-testid="stat-selected-week">
              {/* Label row */}
              <div className="flex items-baseline gap-3 mb-1">
                <p className="flex-1 text-xs text-muted-foreground">{selectedLabel}</p>
                {hasYearAgo       && <span className="w-16 text-right text-xs text-muted-foreground">vs {meta.yearAgoYear}</span>}
                {hasPreviousWeek  && <span className="w-16 text-right text-xs text-muted-foreground">vs week {meta.previousWeekNum}</span>}
              </div>
              {/* Value row */}
              <div className="flex items-baseline gap-3">
                <span className="flex-1 text-3xl font-bold text-emerald-500">
                  {totals.totalSelected}
                </span>
                {hasYearAgo && (
                  <div className="w-16 flex items-baseline justify-end gap-0.5">
                    <span className="text-2xl font-semibold text-amber-500">{totals.partialYearAgo}</span>
                    <GrowthBadge current={totals.totalSelected} previous={totals.partialYearAgo} />
                  </div>
                )}
                {hasPreviousWeek && (
                  <div className="w-16 flex items-baseline justify-end gap-0.5">
                    <span className="text-2xl font-semibold text-blue-500">{totals.partialPrevious}</span>
                    <GrowthBadge current={totals.totalSelected} previous={totals.partialPrevious} />
                  </div>
                )}
              </div>
            </div>

            {/* ── Previous week ── */}
            {hasPreviousWeek && (
              <div data-testid="stat-previous-week">
                <p className="text-xs text-muted-foreground mb-1">{previousLabel}</p>
                <span className="text-3xl font-bold text-blue-500">{totals.totalPrevious}</span>
              </div>
            )}

            {/* ── Same week last year ── */}
            {hasYearAgo && (
              <div data-testid="stat-year-ago">
                <p className="text-xs text-muted-foreground mb-1">{yearAgoLabel}</p>
                <span className="text-3xl font-bold text-amber-500">{totals.totalYearAgo}</span>
              </div>
            )}

          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function GrowthBadge({ current, previous }: { current: number; previous: number }) {
  const pct = pctChange(current, previous)
  const label = pct !== null ? `${Math.abs(pct)}%` : ''

  if (current > previous) {
    return (
      <span className="text-xs font-medium text-emerald-500">↑{label}</span>
    )
  }
  if (current < previous) {
    return (
      <span className="text-xs font-medium text-red-500">↓{label}</span>
    )
  }
  return (
    <span className="text-xs font-medium text-muted-foreground">=0%</span>
  )
}
